'use client'

import React, { useState, useEffect } from 'react'
import { CHANGELOG_QUERYResult, POST_QUERYResult } from '../../../sanity.types'
import clsx from 'clsx'
import { PrimaryButton } from '../Button'
import { getStartedLink } from '@/config'
import Link from 'next/link'
import { Lexend } from 'next/font/google'
import { slugify } from './PortableTextComponents'

type HeadingsType =
  | NonNullable<POST_QUERYResult | CHANGELOG_QUERYResult>['headings']
  | undefined
type HeadingBlock = NonNullable<HeadingsType>[number]

const lexend = Lexend({
  subsets: ['latin'],
  weight: ['300', '400', '500', '700'],
  display: 'swap',
})

const TableOfContents = ({
  headings,
  showCTA,
}: {
  headings: HeadingsType
  showCTA: boolean
}) => {
  const [activeId, setActiveId] = useState<string>('')

  // console.log('heading', headings?.[0]?.children?.[0]?.text)
  // console.log('heading slug', slugify(headings?.[0]?.children?.[0]?.text ?? ''))

  // const isActive = (heading: HeadingBlock | null | undefined): boolean => {
  //   if (!heading?.children?.[0]?.text) return false
  //   const headingId = slugify(heading.children[0].text)
  //   return headingId === activeId
  // }

  useEffect(() => {
    const handleScroll = () => {
      const headingElements = document.querySelectorAll('h2, h3, h4, h5, h6')

      let current = ''

      headingElements.forEach((section) => {
        const sectionTop = section.getBoundingClientRect().top

        if (sectionTop < 100) {
          current = section.getAttribute('id') || ''
        }
      })

      setActiveId(current)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  type TOCEntry = {
    id: string
    text: string
    children: TOCEntry[]
  }

  const groupHeadings = (headings: HeadingBlock[] = []): TOCEntry[] => {
    const toc: TOCEntry[] = []

    headings.forEach((heading) => {
      const text = heading.children?.[0]?.text || ''
      const id = slugify(text)
      const entry = { id, text, children: [] }

      if (heading.style === 'h2') {
        toc.push(entry)
      } else if (heading.style === 'h3' && toc.length > 0) {
        toc[toc.length - 1].children.push(entry)
      }
    })

    return toc
  }

  const groupedHeadings = groupHeadings(headings || [])
  const isActiveId = (id: string, children?: TOCEntry[]) => {
    if (id === activeId) return true
    return children?.some((child) => child.id === activeId) ?? false
  }

  return (
    <div
      className={clsx(
        'mb-12 hidden space-y-5 xl:sticky xl:top-12 xl:-mr-6 xl:block xl:h-fit xl:flex-none xl:overflow-y-auto xl:pr-6 xl:pt-16',
        'w-72'
      )}
    >
      <nav aria-labelledby="on-this-page-title">
        <>
          <h2
            id="on-this-page-title"
            className={`${lexend.className} flex items-center gap-x-2 text-sm font-medium text-slate-400`}
          >
            <svg
              className="fill-slate-400"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              role="img"
              focusable="false"
              aria-hidden="true"
              style={
                {
                  '--icon-color': 'var(--color-text-tertiary)',
                } as React.CSSProperties
              }
            >
              <path d="M2 3.5a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 2 3.5ZM2 8a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 8Zm.75 3.75a.75.75 0 0 0 0 1.5h5.5a.75.75 0 0 0 0-1.5h-5.5Z"></path>
            </svg>
            Contents
          </h2>
          <ol role="list" className="ml-1 mt-4 text-sm">
            {groupedHeadings.map((h2) => (
              <li
                key={h2.id}
                className={clsx(
                  isActiveId(h2.id, h2.children)
                    ? 'border-l-2 border-sky-500'
                    : 'border-l-2 border-slate-800',
                  'py-2'
                )}
              >
                <div className="pl-3">
                  <Link
                    href={`#${h2.id}`}
                    className={clsx(
                      isActiveId(h2.id, h2.children)
                        ? 'text-sky-500'
                        : 'font-normal text-slate-400 hover:text-slate-300'
                    )}
                  >
                    {h2.text}
                  </Link>

                  {h2.children.length > 0 && (
                    <ol
                      role="list"
                      className="mt-2 space-y-2 pl-4 text-slate-400"
                    >
                      {h2.children.map((h3) => (
                        <li
                          key={h3.id}
                          className={clsx(
                            h3.id === activeId
                              ? 'text-sky-500'
                              : 'hover:text-slate-300'
                          )}
                        >
                          <Link href={`#${h3.id}`}>{h3.text}</Link>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </>
      </nav>
      {showCTA && (
        <div className="flex flex-col items-center rounded-lg bg-slate-800/60 p-4 pb-6">
          <h3
            className={`${lexend.className} text-center text-xl font-medium text-sky-400`}
          >
            Optimize your Snowflake usage
          </h3>

          <p className="mb-4 mt-2 break-normal text-center text-sm text-slate-300 ">
            SELECT automatically optimizes and helps you manage your Snowflake
            usage with ease.
          </p>
          <PrimaryButton textSize="text-sm" href={getStartedLink}>
            Try for free
          </PrimaryButton>
        </div>
      )}
    </div>
  )
}
export default TableOfContents
